import Fingerprint2 from 'fingerprintjs2';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import Cookies from 'js-cookie';
import { db, appCheck } from './firebaseConfig';
import { getToken } from 'firebase/app-check';
import axios from 'axios';

/**
 * Checks if a device has already been used for a free trial signup.
 * It checks for a 'burned' record (hits: 0) in fingerprintHits, ipHits, or cookieHits.
 * @returns {Promise<{ availableHits: number; success: boolean }>} 
 * - availableHits will be 0 if the device is blocked.
 * - availableHits will be 1 if the device is new/not blocked.
 */
export const trackHits = async (
  action = 'check', // action parameter is kept for consistency but logic is simplified
  ipInfo?: { ip?: string }
): Promise<{ availableHits: number; success: boolean }> => {
  try {
    if (appCheck) {
      try {
        await getToken(appCheck);
      } catch (appCheckError) {
        console.error("❌ App Check token verification failed during trackHits:", appCheckError);
      }
    }
    
    // 1. Fingerprinting Check
    const fingerprintPromise = new Promise<number | undefined>((resolve) => {
      Fingerprint2.get((components) => {
        const values = components.map((component) => component.value);
        const fingerprint = Fingerprint2.x64hash128(values.join(''), 31);

        let deviceId = localStorage.getItem('deviceId');
        if (!deviceId) {
          deviceId = `${fingerprint}`;
          localStorage.setItem('deviceId', deviceId);
        }

        const fingerprintRef = doc(db, 'fingerprintHits', deviceId);
        getDoc(fingerprintRef)
          .then((snap) => resolve(snap.exists() ? snap.data()?.hits : undefined))
          .catch(() => resolve(undefined));
      });
    });

    const getIp = async () => {
      try {
        const res = await axios.get('https://ipapi.co/json/', { timeout: 5000 });
        return res.data.ip;
      } catch (err) {
        console.warn("IP fetch failed (ipapi.co), falling back to geolocation-db...");
        try {
           const res = await axios.get('https://geolocation-db.com/json/', { timeout: 3000 });
           return res.data.IPv4;
        } catch (fallbackErr) {
           return undefined;
        }
      }
    };

    // 2. IP Address Check
    const getIpHits = async (): Promise<number | undefined> => {
      const ip = await getIp();
      if (!ip) return undefined;

      localStorage.setItem('ip', ip);
      const ipId = ip.replace(/\./g, '_');
      const ipRef = doc(db, 'ipHits', ipId);
      const ipSnap = await getDoc(ipRef);
      return ipSnap.exists() ? ipSnap.data()?.hits : undefined;
    };

    // 3. Cookie-Based Check
    let cookieId = Cookies.get('trackingId');
    if (!cookieId) {
      cookieId = `${Date.now()}_${Math.random().toString(36).substr(2)}`;
      localStorage.setItem('cookie', cookieId);
      Cookies.set('trackingId', cookieId, { expires: 365, secure: true, sameSite: 'Strict' });
    }
    localStorage.setItem('cookie', cookieId);
    const cookieRef = doc(db, 'cookieHits', cookieId);
    const cookieHitsPromise = getDoc(cookieRef)
      .then((snap) => (snap.exists() ? snap.data()?.hits : undefined))
      .catch(() => undefined);

    // --- LOGIC TO DETERMINE IF DEVICE IS BLOCKED ---
    const ipHitsPromise = getIpHits();
    const allHits = await Promise.all([fingerprintPromise, ipHitsPromise, cookieHitsPromise]);
    
    // If any of the tracking methods found a record with 0 hits, the device is blocked.
    const isDeviceBlocked = allHits.some(hitCount => hitCount === 0);

    if (isDeviceBlocked) {
      return { availableHits: 0, success: true }; // Device is blocked
    }

    // If no record was found with 0 hits, the device is considered new/not blocked.
    return { availableHits: 1, success: true }; // Device is NOT blocked

  } catch (err) {
    console.error('Error in trackHits:', err);
    // Default to NOT blocked in case of an error to avoid preventing legitimate signups.
    return { availableHits: 1, success: false };
  }
};

/**
 * Marks the current device as "used" by creating records in Firestore with hits set to 0.
 * This function should be called immediately after a successful free trial signup.
 */
export const markDeviceAsUsed = async (): Promise<void> => {
  try {
    if (appCheck) {
      try {
        await getToken(appCheck);
      } catch (appCheckError) {
        console.error("❌ App Check token verification failed during device marking:", appCheckError);
      }
    }
    
    // 1. Get and "Burn" Fingerprint
    const components = await new Promise<any[]>(resolve => Fingerprint2.get(resolve));
    const values = components.map(component => component.value);
    const fingerprint = Fingerprint2.x64hash128(values.join(''), 31);
    const fingerprintRef = doc(db, 'fingerprintHits', fingerprint);
    // Corrected: Replaced new Date() with serverTimestamp()
    await setDoc(fingerprintRef, { hits: 0, registeredAt: serverTimestamp() }, { merge: true });

    // 2. Get and "Burn" IP Address
    try {
       let ip = undefined;
       try {
          const ipResponse = await axios.get('https://ipapi.co/json/', { timeout: 5000 });
          ip = ipResponse.data.ip;
       } catch (e) {
          const fallback = await axios.get('https://geolocation-db.com/json/', { timeout: 3000 });
          ip = fallback.data.IPv4;
       }
       
       if (ip) {
         const ipId = ip.replace(/\./g, '_');
         const ipRef = doc(db, 'ipHits', ipId);
         // Corrected: Replaced new Date() with serverTimestamp()
         await setDoc(ipRef, { hits: 0, registeredAt: serverTimestamp() }, { merge: true });
       }
    } catch (ipError) {
       console.error("Failed to burn IP:", ipError);
    }

    // 3. Get and "Burn" Cookie
    const cookieId = Cookies.get('trackingId');
    if (cookieId) {
      const cookieRef = doc(db, 'cookieHits', cookieId);
      // Corrected: Replaced new Date() with serverTimestamp()
      await setDoc(cookieRef, { hits: 0, registeredAt: serverTimestamp() }, { merge: true });
    }

  } catch (error) {
    console.error("Error marking device as used:", error);
    // We don't throw an error here to avoid breaking the signup flow, but we log it.
  }
};
